import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, Package, Receipt, Clock3, Users,
  ScrollText, ShieldCheck, LogOut, Menu, Store, MapPin, Wifi, WifiOff,
  Landmark, Contact, CreditCard, AlertTriangle, Wallet, ShoppingBag, Settings, X,
} from "lucide-react";
import api, { mediaUrl } from "../../lib/api";
import BrandLogo from "../BrandLogo";
import { useAuth } from "../../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, module: "dashboard", roles: ["owner", "manager", "system_admin"] },
  { to: "/pos", label: "Point of Sale", icon: ShoppingCart, module: "pos", roles: ["owner", "manager", "cashier", "system_admin"] },
  { to: "/inventory", label: "Inventory", icon: Package, module: "inventory", roles: ["owner", "manager", "system_admin"] },
  { to: "/sales", label: "Invoice Register", icon: Receipt, module: "sales", roles: ["owner", "manager", "cashier", "system_admin"] },
  { to: "/orders", label: "Client Orders", icon: ShoppingBag, module: "orders", roles: ["owner", "manager", "cashier", "system_admin"] },
  { to: "/attendance", label: "Attendance", icon: Clock3, module: "attendance", roles: ["owner", "manager", "cashier", "system_admin"] },
  { to: "/customers", label: "Clients", icon: Contact, module: "clients", roles: ["owner", "manager", "cashier", "system_admin"] },
  { to: "/staff", label: "Staff & Branches", icon: Users, module: "staff", roles: ["owner", "manager", "system_admin"] },
  { to: "/payroll", label: "Payroll", icon: Wallet, module: "payroll", roles: ["owner", "manager", "system_admin"] },
  { to: "/bir", label: "BIR & Tax", icon: Landmark, module: "bir", roles: ["owner", "manager", "system_admin"] },
  { to: "/subscription", label: "Subscription", icon: CreditCard, module: "subscription", roles: ["owner", "manager", "system_admin"] },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, module: "audit", roles: ["owner", "manager", "system_admin"] },
  { to: "/settings", label: "Settings", icon: Settings, module: null, roles: ["owner", "manager", "system_admin"] },
  { to: "/admin", label: "Admin Console", icon: ShieldCheck, module: null, roles: ["system_admin"] },
];

const ROLE_STYLES = {
  system_admin: "bg-slate-900 text-white",
  owner: "bg-emerald-100 text-emerald-800",
  manager: "bg-blue-100 text-blue-800",
  cashier: "bg-amber-100 text-amber-800",
};

export function NeedTenant() {
  return (
    <div className="py-24 text-center space-y-3" data-testid="need-tenant-notice">
      <Store className="h-10 w-10 mx-auto text-slate-300" />
      <p className="text-sm text-slate-500">Select a store from the header selector to manage this module.</p>
    </div>
  );
}

function NavItems({ onNavigate }) {
  const { user, canAccessModule } = useAuth();
  const visible = NAV.filter((n) =>
    n.roles.includes(user.role) && (n.module === null || canAccessModule(n.module))
  );
  return (
    <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
      {visible.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`
          }
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell({ children }) {
  const { user, tenant, branch, subscription, activeTenantId, setActiveTenantId, setTenant, setBranch, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [time, setTime] = useState(new Date());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminTenants, setAdminTenants] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [promoBanners, setPromoBanners] = useState([]);
  const [promoDismissed, setPromoDismissed] = useState(false);
  const promoTenantId = user.role === "system_admin" ? activeTenantId : user.tenant_id;

  useEffect(() => {
    setPromoDismissed(false);
    if (!promoTenantId) { setPromoBanners([]); return; }
    api.get(`/portal/gallery?tenant_id=${promoTenantId}`)
      .then(({ data }) => setPromoBanners(data.banners || []))
      .catch(() => setPromoBanners([]));
  }, [promoTenantId]);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    if (user.role !== "system_admin") return;
    api.get("/admin/tenants").then(({ data }) => setAdminTenants(data)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    if (user.role === "system_admin" && activeTenantId && adminTenants.length) {
      const t = adminTenants.find((x) => x.tenant_id === activeTenantId);
      if (t) setTenant(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTenants]);

  const handleTenantSelect = (id) => {
    setActiveTenantId(id);
    setTenant(adminTenants.find((t) => t.tenant_id === id) || null);
    setBranch(null);
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (subscription?.locked && user.role !== "system_admin" && location.pathname !== "/subscription") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6" data-testid="subscription-lock-screen">
        <div className="w-full max-w-md bg-white border border-red-200 rounded-2xl p-10 text-center space-y-5 shadow-sm">
          <div className="mx-auto h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-red-600" />
          </div>
          <div className="space-y-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Subscription expired</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              All modules are locked for <span className="font-semibold text-slate-700">{tenant?.store_name}</span>.
              Please renew the subscription to continue.
            </p>
          </div>
          <Button className="w-full bg-slate-900" onClick={() => navigate("/subscription")} data-testid="lock-goto-subscription-btn">
            View subscription &amp; plans
          </Button>
          <Button variant="ghost" className="w-full" onClick={doLogout} data-testid="lock-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col border-r border-slate-200 bg-white z-30">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-100">
          <BrandLogo />
          <span className="font-heading font-bold text-lg tracking-tight text-slate-900">MatteusMx</span>
        </div>
        <NavItems />
        <div className="border-t border-slate-100 p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-700">
              {user.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.username || user.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={doLogout} data-testid="sidebar-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-md">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 flex flex-col">
                <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-100">
                  <BrandLogo />
                  <span className="font-heading font-bold text-lg tracking-tight">MatteusMx</span>
                </div>
                <NavItems onNavigate={() => setMobileOpen(false)} />
                <div className="border-t border-slate-100 p-4 mt-auto">
                  <Button variant="outline" className="w-full" onClick={doLogout}>
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            {user.role === "system_admin" && (
              <Select value={activeTenantId || ""} onValueChange={handleTenantSelect}>
                <SelectTrigger className="w-52 h-9" data-testid="admin-tenant-selector">
                  <SelectValue placeholder="Select store…" />
                </SelectTrigger>
                <SelectContent>
                  {adminTenants.map((t) => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id} data-testid={`admin-tenant-option-${t.tenant_id}`}>
                      {t.store_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {tenant && (
              <div className="hidden sm:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5" data-testid="header-tenant-badge">
                <Store className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-xs font-semibold text-slate-800">{tenant.store_name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{tenant.business_type}</span>
              </div>
            )}
            {branch && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500" data-testid="header-branch-label">
                <MapPin className="h-3.5 w-3.5" />
                {branch.branch_name}
              </div>
            )}

            <div className="flex-1" />

            {!online && (
              <span className="flex items-center gap-1.5 rounded-full bg-red-100 border border-red-200 px-2.5 py-1 text-[11px] font-semibold text-red-700" data-testid="offline-indicator">
                <WifiOff className="h-3 w-3" /> Offline
              </span>
            )}
            {online && (
              <span className="hidden md:flex items-center gap-1.5 text-xs text-slate-500" data-testid="online-indicator">
                <Wifi className="h-3.5 w-3.5 text-emerald-500" />
              </span>
            )}
            {subscription && !subscription.locked && subscription.days_remaining !== null
              && subscription.days_remaining <= (subscription.plan_id === "free" ? 2 : 7)
              && user.role !== "system_admin" && (
              <Link to="/subscription" className="hidden sm:flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 px-2.5 py-1 text-[11px] font-semibold text-amber-800" data-testid="subscription-reminder-chip">
                <Clock3 className="h-3 w-3" /> Renews in {subscription.days_remaining}d
              </Link>
            )}
            <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-500 font-mono" data-testid="header-live-clock">
              {time.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${ROLE_STYLES[user.role] || "bg-slate-100 text-slate-700"}`} data-testid="header-role-pill">
              {user.role?.replace("_", " ")}
            </span>
          </div>
        </header>
        {promoBanners.length > 0 && !promoDismissed && (
          <div className="relative border-b border-slate-200" data-testid="promo-banner-strip">
            <img src={mediaUrl(promoBanners[0].image_id)} alt={promoBanners[0].caption || "Store promotion"}
              className="w-full h-14 sm:h-16 object-cover" />
            {promoBanners[0].caption && (
              <span className="absolute inset-0 flex items-center justify-center text-white text-sm font-semibold drop-shadow-md bg-slate-900/30 px-10 text-center">{promoBanners[0].caption}</span>
            )}
            <button onClick={() => setPromoDismissed(true)} data-testid="promo-banner-dismiss"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1 text-slate-600 hover:text-slate-900">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
